// Panel "Características" del formulario: etiquetas, avisos, listas, propietario,
// respuesta automática, agradecimiento/redirección y creación de empresa.
import type { JSX } from "react";
import { Field, Input, Select, Textarea } from "../../../components/ui.tsx";
import { CheckList, ChipsInput, Toggle, type FormSettings, type MktOptions } from "./shared.tsx";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function FormSettingsPanel({ value, onChange, options, disabled }: {
  value: FormSettings; onChange: (v: FormSettings) => void; options: MktOptions | undefined; disabled?: boolean;
}): JSX.Element {
  const set = (patch: Partial<FormSettings>): void => onChange({ ...value, ...patch });
  const assign = value.assign ?? { mode: "default" as const };
  const auto = value.autoReply ?? { enabled: false };
  return (
    <fieldset disabled={disabled} className="space-y-5">
      <section className="space-y-3">
        <h4 className="sec-title text-sm">Contacto</h4>
        <Field label="Etiquetas para el contacto" hint="Se añaden al contacto en cada envío (Enter para agregar).">
          <ChipsInput values={value.tags ?? []} onChange={(tags) => set({ tags })} placeholder="p. ej. web, seminario-2026" />
        </Field>
        <Field label="Añadir a listas de contactos">
          <CheckList items={options?.lists ?? []} selected={value.listIds ?? []} onChange={(listIds) => set({ listIds })} empty="Aún no hay listas de contactos." />
        </Field>
        <Toggle checked={value.createAccount !== false} onChange={(v) => set({ createAccount: v })} label="Crear empresa"
          hint="Si el contacto indica una empresa que no existe, se crea y se vincula." />
      </section>

      <section className="space-y-3">
        <h4 className="sec-title text-sm">Propietario de contactos nuevos</h4>
        <Select value={assign.mode} onChange={(e) => set({ assign: { ...assign, mode: e.target.value as "default" | "fixed" | "roundrobin" } })}>
          <option value="default">Por defecto (administrador principal)</option>
          <option value="fixed">Usuario fijo</option>
          <option value="roundrobin">Rotativo (round-robin) entre usuarios</option>
        </Select>
        {assign.mode === "fixed" && (
          <Select value={assign.userId ?? ""} onChange={(e) => set({ assign: { ...assign, userId: e.target.value || null } })}>
            <option value="">Elige un usuario…</option>
            {(options?.users ?? []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </Select>
        )}
        {assign.mode === "roundrobin" && (
          <CheckList items={options?.users ?? []} selected={assign.userIds ?? []} onChange={(userIds) => set({ assign: { ...assign, userIds } })} empty="No hay usuarios activos." />
        )}
      </section>

      <section className="space-y-3">
        <h4 className="sec-title text-sm">Avisos internos</h4>
        <Field label="Correos de aviso" hint="Reciben un resumen de cada envío (máx. 10).">
          <ChipsInput values={value.notifyEmails ?? []} onChange={(notifyEmails) => set({ notifyEmails })} max={10} maxLen={160}
            placeholder="direccion.comunicaciones@sacsconsultores.com" validate={(v) => (EMAIL_RE.test(v) ? null : "Correo no válido.")} />
        </Field>
        <p className="text-xs text-ink-muted">El propietario del contacto recibe además una notificación en Tiger.</p>
      </section>

      <section className="space-y-3">
        <h4 className="sec-title text-sm">Después de enviar</h4>
        <Field label="Mensaje de agradecimiento">
          <Textarea rows={2} maxLength={1000} value={value.thankYou ?? ""} onChange={(e) => set({ thankYou: e.target.value })} placeholder="¡Gracias! Hemos recibido tus datos." />
        </Field>
        <Field label="O redirigir a una URL" hint="Si se indica, se redirige en lugar de mostrar el mensaje.">
          <Input type="url" maxLength={500} value={value.redirectUrl ?? ""} onChange={(e) => set({ redirectUrl: e.target.value.trim() })} placeholder="https://sacsconsultores.com/gracias" />
        </Field>
      </section>

      <section className="space-y-3">
        <h4 className="sec-title text-sm">Respuesta automática</h4>
        <Toggle checked={auto.enabled} onChange={(v) => set({ autoReply: { ...auto, enabled: v } })} label="Enviar correo al contacto" hint="Requiere un campo de correo. Usa {{nombre}} para personalizar." />
        {auto.enabled && (
          <>
            <Field label="Asunto"><Input maxLength={200} value={auto.subject ?? ""} onChange={(e) => set({ autoReply: { ...auto, subject: e.target.value } })} /></Field>
            <Field label="Contenido (HTML)" hint="Se sanea al guardar (sin scripts).">
              <Textarea rows={6} maxLength={50_000} className="font-mono text-xs" value={auto.html ?? ""} onChange={(e) => set({ autoReply: { ...auto, html: e.target.value } })}
                placeholder="<p>Hola {{nombre}},</p><p>Gracias por escribirnos…</p>" />
            </Field>
          </>
        )}
      </section>
    </fieldset>
  );
}
